import { action } from 'mobx'
import fetch from 'node-fetch'
import qs from 'querystring'
import { isServer, jsonToMap, mapToJson } from './utils'
export { default as withMobx } from './with-mobx'

export function getApiURL(isServer, clientURL, ssrURL) {
  if (isServer) {
    return `${ssrURL}` || `${process.env.API_HOST}`
  } else {
    return `${clientURL}` || `${process.env.API_HOST}`
  }
}

// getMethod is make sure that the method is always
// uppercase
const getMethod = (method) => {
  method = method || 'GET'
  method = method.toUpperCase()
  return method
}

const parseHTTPLinks = (linksString) => {
  const links = {}

  linksString
    .trim()
    .split(',')
    .filter((link) => link && link !== '')
    .forEach((link) => {
      var [href, rel] = link.split(';')
      href = href.replace(/<(.*)>/, '$1').trim()
      rel = rel.replace(/rel="(.*)"/, '$1').trim()
      links[rel] = href
    })

  return links
}

const __NEXT_MOBX_STORE__ = new Map()

export class BaseStore {
  constructor(props = {}, options, VERSION) {
    for (const prop in props) {
      // Convert JSON to Map
      this[prop] = jsonToMap(props[prop])
    }

    this.jwt = options && options.jwt
    this.csrfToken = options && options.csrfToken
    this.clientURL = options && options.clientURL
    this.ssrURL = options && options.ssrURL
    this.VERSION = VERSION || 'v2.0'
  }

  @action
  update = (data = {}) => {
    for (const prop in data) {
      this[prop] = data[prop]
    }
  }

  request = async (options) => {
    let reqUrl = `${isServer ? this.ssrURL : this.clientURL}${options.path}`

    if (options.query) {
      // the following code simplified the api
      // query string can be passed in like {q: undefined, b: '', c: null}
      // and it removes before pass it to query string
      let total = 0
      let deleted = 0
      const queryCopy = { ...options.query }
      for (const key in queryCopy) {
        total++
        if (queryCopy[key] === undefined || queryCopy[key] === '' || queryCopy[key] === null) {
          delete queryCopy[key]
          deleted++
        }
      }
      // if no more keys left inside query object
      // don't bother append ? to the url
      const left = total - deleted
      if (left > 0) {
        reqUrl += `?${qs.stringify(queryCopy)}`
      }
    }

    const headers = {
      Accept: `application/vnd.next.${this.VERSION}+json`,
      cookie: `jwt=${this.jwt};`,
      Authorization: `Bearer ${this.jwt}`,
      ['x-csrf-token']: this.csrfToken,
      ...options.headers,
    }

    let response = {
      data: {},
      links: {},
      status: 420, // Default value (>=400) in case we can't send request at all. "420 Enhance Your Calm".
      // error will be set on exceptions.
    }

    let resp
    try {
      resp = await fetch(reqUrl, {
        credentials: 'include',
        headers,
        method: getMethod(options.method),
        body: JSON.stringify(options.data),
      })
      response.status = resp.status
    } catch (e) {
      response.error = e
      throw response
    }

    if (resp.headers && resp.headers.has('Link')) {
      response.links = parseHTTPLinks(resp.headers.get('Link'))
    }

    if (resp.status !== 204) {
      // Get data from the response body.
      try {
        if (options.responseType !== 'text') {
          response.data = await resp.json()
        } else {
          response.data = await resp.text()
        }
      } catch (e) {
        response.error = e
        throw response
      }
    }

    if (resp.status >= 400) {
      if (Object.prototype.hasOwnProperty.call(response.data, 'error')) {
        response = {
          ...response,
          ...response.data, // Spread response.data fields (error, error_context, error_stack, vctraceid, etc.) into the response object.
        }
      }
      throw response
    }

    return response
  }
}

export const getOrCreateStore =
  (storeKeyName, Store, VERSION) => (initialState, options, VERSION) => {
    // Convert Map to JSON
    if (initialState) {
      for (const itemState in initialState) {
        try {
          const dataMap = initialState[itemState]
          if (dataMap.toJS() instanceof Map) {
            initialState[itemState] = mapToJson(dataMap)
          }
        } catch (e) {
          // nothing
        }
      }
    }

    // Always make a new store if server
    if (isServer) {
      return new Store(initialState, options, VERSION)
    }

    // Create store if unavailable on the client
    if (!__NEXT_MOBX_STORE__.has(storeKeyName)) {
      __NEXT_MOBX_STORE__.set(storeKeyName, new Store(initialState, options, VERSION))
    }

    return __NEXT_MOBX_STORE__.get(storeKeyName)
  }
